import type * as Ariakit from '@ariakit/react/menu';
import cn from 'classnames';
import { Menu, MenuButton, useMenuStore } from '#components/patterns/menus/menu/index.ts';
import styles from './MeatballMenu.module.css';

type Props = {
  children?: React.ReactNode | ((menu: Ariakit.MenuStore) => React.ReactNode);
  className?: string;
  'aria-label'?: string;
  disabled?: boolean;
  gutter?: number;
  portal?: boolean;
  /** {@link menuButtonVariant} allows disabling a couple of classes and size restrictions with the `minimal` variant. */
  menuButtonVariant?: 'default' | 'minimal';
  /** {@link menuButtonTheme} allows setting `data-user-color-theme` as an override on the menu. */
  'data-user-color-scheme'?: 'light' | 'dark';
  render?: React.ReactElement;
  menuStoreProps?: Ariakit.MenuStoreProps;
} & Omit<React.ComponentPropsWithRef<'button'>, 'children'>;

export function MeatballMenu({
  children,
  className,
  disabled,
  'aria-label': ariaLabel = 'More',
  gutter,
  menuButtonVariant = 'default',
  'data-user-color-scheme': colorScheme,
  portal = true,
  render,
  /** {@link props} allows us to conform with <https://ariakit.org/guide/composition#custom-components-must-be-open-for-extension> */
  ...props
}: Props) {
  const menu = useMenuStore();

  return (
    <>
      <MenuButton
        {...props}
        store={menu}
        className={cn('btn', {
          'icon-btn': menuButtonVariant === 'default',
          [styles.MeatballMenu_button]: menuButtonVariant === 'default',
        })}
        aria-label={ariaLabel}
        disabled={disabled}
        render={render}
      >
        <Meatballs />
      </MenuButton>
      <Menu
        store={menu}
        className={cn('menu-list', className)}
        portal={portal}
        unmountOnHide
        gutter={gutter}
        data-user-color-scheme={colorScheme}
      >
        {typeof children === 'function' ? children(menu) : children}
      </Menu>
    </>
  );
}

function Meatballs() {
  return (
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="3" cy="12" r="3" />
      <circle cx="12" cy="12" r="3" />
      <circle cx="21" cy="12" r="3" />
    </svg>
  );
}
